<template>
   <b-container fluid class="pr-xl-5 pr-sm-0 pl-xl-5 pl-sm-0">
      <b-row class="mt-5 mb-4 dashboard-title">
         <b-col class="d-flex">
            <h2 class="d-inline-block" style="font-size: 1.25rem; font-weight: 700">My Clients</h2>
            <b-button
               v-if="!isExternalPartner"
               id="btn-new-company"
               variant="success"
               :to="{name: 'company-create'}"
               class="dashboard__add-new rounded"
            >
               <b-icon-plus></b-icon-plus>
            </b-button>
         </b-col>
         <b-col class="d-flex justify-content-end align-items-center">
            <StaffTableFilter @onStageChange="onStageChange"></StaffTableFilter>
            <div style="width: 17rem">
               <b-input
                  class="col"
                  style="font-size: 12px; font-weight: normal"
                  type="text"
                  placeholder="Search"
                  v-model="searchFilter"
               ></b-input>
            </div>
         </b-col>
      </b-row>

      <b-row v-if="loading">
         <b-col class="loading-container">
            <b-spinner variant="primary" class="loading-spinner"></b-spinner>
         </b-col>
      </b-row>
      <b-row v-else class="dashboard-table">
         <StaffDashboardTable
            :companies="filteredCompanies"
            :is-filtered="isFiltered"
            :has-companies="baseCompanies.length > 0"
            @onSortChange="onSortChange"
         ></StaffDashboardTable>
      </b-row>

      <ProspectLookupTool v-if="isExternalPartner || isRndig"></ProspectLookupTool>
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';
import {sortBy} from '@/helpers/utils';
import StaffDashboardTable from '@/views/dashboards/widgets/StaffDashboardTable.vue';
import StaffTableFilter from '@/views/dashboards/widgets/StaffTableFilter.vue';
import ProspectLookupTool from '@/views/dashboards/widgets/ProspectLookupTool.vue';
import {StudyStage} from '../../store/models/study';

export default {
   components: {
      StaffDashboardTable,
      StaffTableFilter,
      ProspectLookupTool,
   },

   async created() {
      this.loading = true;
      try {
         await Promise.all([
            this.$store.dispatch('companies/loadCompanies'),
            this.$store.dispatch('users/loadUsers'),
         ]);
         this.companyData = this.$store.getters['companies/companies'];
         this.companyData.sort(sortBy('companyName', (name) => name.toUpperCase()));
      } catch (err) {
         this.$store.commit('showAlert', {
            response: err.response,
            fallbackMsg: 'Failed to fetch companies',
            seconds: 5,
         });
      } finally {
         this.loading = false;
      }
   },

   data() {
      return {
         companyData: [],
         loading: false,
         showAllCompanies: false,
         stageFilters: [],
         searchFilter: '',
         sortParams: {
            columnName: 'clientName',
            order: 'ascending',
         },
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         isExternalPartner: 'isExternalPartner',
         isRndig: 'isRndig',
      }),

      // The company list, including the current user's role
      companies() {
         return this.companyData.map((company) => {
            let role = 'None';
            if (company.businessManagers.includes(this.profile.id)) {
               role = 'Business Manager';
            } else if (company.projectManagers.includes(this.profile.id)) {
               role = 'Project Manager';
            } else if (company.projectAssociates.includes(this.profile.id)) {
               role = 'Project Associate';
            } else if (company.externalPartners.includes(this.profile.id)) {
               role = 'External Partners';
            }
            return {
               ...company,
               role,
            };
         });
      },

      baseCompanies() {
         if (this.showAllCompanies) {
            return this.companies;
         }
         return this.companies.filter((company) => company.role !== 'None');
      },

      isFiltered() {
         return this.stageFilters.length > 0 || this.searchFilter.length > 0;
      },

      filteredCompanies() {
         let companies = this.baseCompanies;

         if (this.stageFilters.length > 0) {
            const includeNoStudy = this.stageFilters.includes(StudyStage.NO_OPEN_STUDY);
            const stageFilters = this.stageFilters.filter((s) => s !== StudyStage.NO_OPEN_STUDY);
            companies = companies.filter(
               (company) =>
                  (includeNoStudy && company.openStudies.length === 0) ||
                  company.openStudies.some((study) => stageFilters.includes(study.stage))
            );
         }

         if (this.searchFilter.length > 0) {
            companies = this.baseCompanies.filter((company) =>
               company.companyName.toLowerCase().includes(this.searchFilter.toLowerCase())
            );
         }

         return this.sortCompanies(companies, this.sortParams);
      },
   },

   methods: {
      onStageChange(selectedStages) {
         this.stageFilters = selectedStages;
      },

      sortCompanies(companies, sortParams) {
         switch (sortParams.columnName) {
            case 'clientName':
               companies.sort((a, b) => {
                  if (sortParams.order === 'ascending') {
                     return a.companyName.localeCompare(b.companyName);
                  } else {
                     return b.companyName.localeCompare(a.companyName);
                  }
               });
               break;
            case 'filingDeadline':
               companies.sort((a, b) => {
                  const first = (a.filingDeadlineMonth || 0) * 100 + (a.filingDeadlineDay || 0);
                  const second = (b.filingDeadlineMonth || 0) * 100 + (b.filingDeadlineDay || 0);
                  if (sortParams.order === 'ascending') {
                     return first - second;
                  } else {
                     return second - first;
                  }
               });
               break;
         }
         return companies;
      },

      onSortChange(sortParams) {
         this.sortParams = sortParams;
      },
   },
};
</script>

<style lang="scss" scoped>
.loading-container {
   display: flex;
   align-items: center;
   justify-content: center;
   height: 10rem;
}

.dashboard-title {
   @media (max-width: 1240px) {
      margin-right: 0 !important;
      margin-left: 0 !important;
   }
}

.dashboard-table {
   @media (max-width: 1240px) {
      margin-right: 0 !important;
      margin-left: 0 !important;
   }
}

.dashboard {
   &__add-new {
      width: 24px;
      height: 24px;
      margin: 0 0 0 0.5rem;
      vertical-align: top;
      font-size: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
   }
}
</style>
